import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery } from '@tanstack/react-query'

import { bucketsAdapter } from '@/services/adapters/buckets'
import { hydrateMapGQL, searchProductGQL } from '@/services/graphql'
import { getAmplifyClient } from '@/services/amplifyClient'
import type { LocationInput, SearchProductFilter } from '@/types/search'
import { transactionalQueryPreset } from '@/services/query-presets'
const client = getAmplifyClient()

const hasPositiveStatusScope = (filter?: SearchProductFilter): boolean => {
  if (!filter) return false
  if (
    filter.byUserID?.eq ||
    filter.organizationID?.eq ||
    filter.organizationMasterID?.eq ||
    (typeof filter.projectID === 'object' &&
      filter.projectID !== null &&
      'eq' in filter.projectID &&
      Boolean(filter.projectID.eq))
  ) {
    return true
  }
  return Boolean(filter.or?.some(hasPositiveStatusScope))
}

export const useDashboard = ({ currentFilter, previousFilter }) => {
  const hasScope = hasPositiveStatusScope(currentFilter)
  return useQuery({
    queryKey: ['dashboard', currentFilter, previousFilter],
    queryFn: () =>
      countProducts({
        currentFilter,
        previousFilter,
      }),
    enabled: !!currentFilter && hasScope,
    ...transactionalQueryPreset,
    placeholderData: {
      currentItems: [],
      previousItems: [],
    },
  })
}

type SearchProductAggregates = {
  searchCurrentProducts: {
    currentItems: {
      name: string
      type: string
      result: {
        buckets: {
          key: string
          doc_count: number
        }[]
      }
    }
  }
  searchPreviousProducts: {
    previousItems: {
      name: string
      type: string
      result: {
        buckets: {
          key: string
          doc_count: number
        }[]
      }
    }
  }
}

const countProducts = async ({ currentFilter, previousFilter }) => {
  try {
    const {
      data: {
        searchCurrentProducts: { currentItems },
        searchPreviousProducts: { previousItems },
      },
    } = (await client.graphql({
      query: searchProductGQL,
      variables: {
        currentFilter,
        previousFilter,
        aggregates: [{ field: 'action', name: 'action', type: 'terms' }],
      },
    })) as GraphQLResult<SearchProductAggregates>
    return {
      currentItems: bucketsAdapter(currentItems),
      previousItems: bucketsAdapter(previousItems),
    }
  } catch (e) {
    console.error('Count products error:', e)
    throw e
  }
}

type HydrateMap = {
  filter: SearchProductFilter
  location?: LocationInput
  distance?: number
  enabled?: boolean
}

export const useHydrateMap = ({
  location,
  filter,
  distance,
  enabled = true,
}: HydrateMap) => {
  const hasScope = hasPositiveStatusScope(filter)
  return useQuery({
    queryKey: ['map', location, filter, distance, enabled],
    queryFn: () =>
      hydrateMap({
        location,
        filter,
        distance,
      }),
    enabled: enabled && hasScope,
    placeholderData: {
      buckets: [],
      total: 0,
    },
  })
}

type SearchProduct = {
  searchProductStatusV2: {
    aggregateItems: {
      name: string
      type: string
      result: {
        buckets: {
          key: string
          doc_count: number
        }[]
      }
    }
    total: number
  }
}

const hydrateMap = async ({
  filter,
  location,
  distance = 5000,
}: HydrateMap) => {
  try {
    const {
      data: {
        searchProductStatusV2: { aggregateItems, total },
      },
    } = (await client.graphql({
      query: hydrateMapGQL,
      variables: {
        location,
        distance: location ? distance : undefined,
        filter,
        aggregates: [
          { field: 'category', name: 'category', type: 'terms' },
          { field: 'condition', name: 'condition', type: 'terms' },
          { field: 'provenance', name: 'provenance', type: 'terms' },
          { field: 'brand', name: 'brand', type: 'terms' },
          { field: 'location', name: 'location', type: 'geohash_grid' },
        ],
      },
    })) as GraphQLResult<SearchProduct>
    return {
      buckets: bucketsAdapter(aggregateItems),
      total,
    }
  } catch (e) {
    console.error('Display map error:', e)
    throw e
  }
}
